import { hasKey, isRecord } from "helpers/typing";

type ServerError = {
    key?: unknown;
    message?: unknown;
};

export type PayloadErrorContent = {
    message?: string;
    description?: string;
};

const getResponseData = (err: unknown): unknown => {
    if (!hasKey(err, "response") || !hasKey(err.response, "data")) {
        return undefined;
    }
    return err.response.data;
};

const readErrorDescription = (value: unknown): string | undefined => {
    if (!isRecord(value) || !hasKey(value, "error") || !isRecord(value.error)) {
        return undefined;
    }
    const { description } = value.error;
    return typeof description === "string" ? description : undefined;
};

/** Raw `error.description` from the API payload (JSON string or plain text). */
export const getRawPayloadErrorDescription = (err: unknown): string | undefined => {
    const responseData = getResponseData(err);
    if (!isRecord(responseData)) return undefined;

    return (
        readErrorDescription(responseData) ??
        (isRecord(responseData.data) ? readErrorDescription(responseData.data) : undefined)
    );
};

/** Parse server `error.description` when it is JSON `{ message, description }`. */
export const parsePayloadErrorDescription = (rawDescription: string): PayloadErrorContent => {
    try {
        const parsed: unknown = JSON.parse(rawDescription);
        if (isRecord(parsed)) {
            return {
                message: typeof parsed.message === "string" ? parsed.message : undefined,
                description: typeof parsed.description === "string" ? parsed.description : undefined,
            };
        }
    } catch {
        // Plain text description
    }
    return { description: rawDescription };
};

export const getPayloadErrorFromResponse = (err: unknown): PayloadErrorContent | undefined => {
    const rawDescription = getRawPayloadErrorDescription(err);
    if (!rawDescription) return undefined;
    return parsePayloadErrorDescription(rawDescription);
};

export const formatPayloadErrorMessage = (payload: PayloadErrorContent): string => {
    const { message, description } = payload;
    if (message && description) {
        return `${message} : ${description}`;
    }
    return description ?? message ?? "";
};

const getServerErrors = (err: unknown): ServerError[] => {
    if (typeof err !== "object" || err === null || !("response" in err)) return [];
    const response = (err as { response?: { data?: unknown } }).response;
    const data = response?.data;
    if (typeof data !== "object" || data === null || !("data" in data)) return [];
    const errors = (data as { data?: unknown }).data;
    if (typeof errors !== "object" || errors === null) return [];

    return Object.values(errors as Record<string, unknown>).filter(
        (value): value is ServerError => typeof value === "object" && value !== null,
    );
};

export const getServerErrorKey = (err: unknown): string | undefined => {
    const error = getServerErrors(err).find((value) => typeof value.key === "string");
    return typeof error?.key === "string" ? error.key : undefined;
};

export const getServerMessage = (err: unknown): string | undefined => {
    const error = getServerErrors(err).find((value) => typeof value.message === "string");
    return typeof error?.message === "string" ? error.message : undefined;
};
